import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Personalidad dimension editors (design/components/bot-config-personality.html).
// PersonalityToneRadios reports/picks the bot's tone; TonePreview shows the line
// the runtime speaks in it, so the operator verifies the tone immediately. All
// config, not code.

// Exactly the two values a stored Tono ('formal' | 'cordial') can produce;
// 'cordial' is shown as "Cercano". A third option, "casual", used to sit here
// with nothing behind it — dropped (no unenforced enum values).
export type ToneValue = 'formal' | 'cercano'

const TONE_ORDER: ToneValue[] = ['formal', 'cercano']

export interface PersonalityToneRadiosProps {
  value: ToneValue
  onChange: (value: ToneValue) => void
  /** Read-only: the bot's formality is DERIVED from the client's tú/usted answer
   *  in the Perfil del negocio, so this radio only REPORTS it. Always render
   *  `hint` alongside — a control that cannot be used must say where it lives. */
  readOnly?: boolean
  /** Pointer copy shown under the group; required reading when `readOnly`. */
  hint?: string
  className?: string
}

/** Radiogroup of tone cards (formal/cercano) for Personalidad, each with an
 *  example line. Roving tabindex; arrow keys move AND select. When `readOnly`
 *  the options stay focusable and readable (aria-disabled, not `disabled`) so
 *  the operator can still see which one is active, and `hint` explains where the
 *  real control is — never a dead-looking control with no explanation. */
export function PersonalityToneRadios({
  value,
  onChange,
  readOnly,
  hint,
  className,
}: PersonalityToneRadiosProps) {
  const { t } = useTranslation('components')
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const hintId = 'tone-hint'
  // A read-only control ALWAYS carries pointer copy; the default names where the
  // real control lives (the client's Perfil del negocio).
  const shownHint = hint ?? (readOnly ? t('personality.toneFromProfile') : undefined)

  function move(currentIndex: number, delta: number) {
    if (readOnly) return
    const next = (currentIndex + delta + TONE_ORDER.length) % TONE_ORDER.length
    const nextTone = TONE_ORDER[next]
    if (!nextTone) return
    onChange(nextTone)
    refs.current[next]?.focus()
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        role="radiogroup"
        aria-label={t('personality.toneGroupLabel')}
        aria-readonly={readOnly ? true : undefined}
        aria-describedby={shownHint ? hintId : undefined}
        className="flex flex-wrap gap-2.5"
      >
        {/* If `value` matches no tone, keep the first option focusable so Tab can
          still enter the group (roving tabindex must never trap the keyboard). */}
        {TONE_ORDER.map((tone, i) => {
          const checked = tone === value
          const focusable = checked || (i === 0 && !TONE_ORDER.includes(value))
          return (
            <button
              key={tone}
              ref={(el) => {
                refs.current[i] = el
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-disabled={readOnly ? true : undefined}
              tabIndex={focusable ? 0 : -1}
              onClick={() => {
                if (!readOnly) onChange(tone)
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault()
                  move(i, 1)
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  move(i, -1)
                }
              }}
              className={cn(
                'flex min-w-[136px] flex-1 flex-col gap-1 rounded-xl border p-3 text-left transition-colors',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
                checked
                  ? 'border-2 border-brand-blue bg-[color-mix(in_srgb,var(--brand-blue)_6%,transparent)]'
                  : 'border-input-line',
                readOnly ? 'cursor-default opacity-70' : !checked && 'hover:border-text-3',
              )}
            >
              <span className="text-[12.5px] font-bold text-text">
                {t(`personality.tones.${tone}.label`)}
              </span>
              <span className="text-[11px] italic text-text-2">
                {t(`personality.tones.${tone}.example`)}
              </span>
            </button>
          )
        })}
      </div>
      {shownHint && (
        <span id={hintId} className="text-[10.5px] leading-snug text-text-3">
          {shownHint}
        </span>
      )}
    </div>
  )
}

export interface TonePreviewProps {
  /** The bot message to show. The caller must pass a line the RUNTIME really
   *  produces for the current tono — the operator console derives it from the
   *  merged config via `greetingReplyForTono`. A hand-written sentence here is a
   *  painted door: it claims to preview a setting it does not read. */
  message: string
  label?: string
  className?: string
}

/** Bubble showing a bot message so the operator verifies the chosen tone
 *  immediately. Presentational only: it renders exactly what the caller passes,
 *  and the caller owes it a real runtime line (see `message`).
 *  aria-live announces tone changes. */
export function TonePreview({ message, label, className }: TonePreviewProps) {
  const { t } = useTranslation('components')
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-text-3">
        {label ?? t('personality.previewLabel')}
      </span>
      {/* aria-live so a tone change is announced to screen-reader users. */}
      <p
        aria-live="polite"
        className="rounded-xl rounded-tl-sm border border-line bg-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-text"
      >
        {message}
      </p>
    </div>
  )
}
